// University Settings Types & Constants

import type { LucideIcon } from "lucide-react";
import type { UniversityState } from "@/store/slices/university";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UniversitySettingKey =
  | "popup"
  | "form"
  | "courses"
  | "higher_qualification"
  | "university_details"
  | "app_config_university"
  | "app_config_app";

// Setting key constants (used for type checking in components)
export const SETTING_KEYS = {
  POPUP: "popup" as const,
  FORM: "form" as const,
  COURSES: "courses" as const,
  HIGHER_QUALIFICATION: "higher_qualification" as const,
  UNIVERSITY_DETAILS: "university_details" as const,
  // DB-backed config tabs (rendered by AppConfigForm, not the variant machinery)
  APP_CONFIG_UNIVERSITY: "app_config_university" as const,
  APP_CONFIG_APP: "app_config_app" as const,
};

export interface SegmentDefinition {
  id: string;
  label: string;
  fields: any[];
}

export interface VariantConfig {
  id: string;
  name: string;
  description: string;
  componentFields: any[];
  /** When set with {@link SegmentWorkbenchForm}, renders tabbed field groups */
  segmentDefinitions?: SegmentDefinition[];
  /** Fields rendered above tabs in {@link SegmentWorkbenchForm} */
  globalSegmentFields?: any[];
  /** Compact mobile-strip CTA (lead drop settings + page mobile section — not shown for popup v6). */
  globalLeadDropMobileStripFields?: any[];
  globalLeadDropMobileStripHelpText?: string;
  metadataFields?: any[];
  showTable: boolean;
  setAction: any;
  formComponent: React.ComponentType<any>;
  previewComponent: any;
  selector: (state: UniversityState) => any;
  createMethod?: (data: any) => Promise<any>;
  updateMethod?: (id: number, data: any) => Promise<any>;
  fetchMethod?: (id: number) => Promise<any>;
  deleteMethod?: (id: number) => Promise<any>;
  hasParentChildStructure?: boolean;
  TREE_CONFIG?: any;
  transformDataForPreview: (data: any) => any;
  constants: Record<string, string>;
}

export interface UniversitySettingConfig {
  key: UniversitySettingKey;
  label: string;
  labelPlural: string;
  description: string;
  icon: LucideIcon;
  iconColor: string;
  iconBgColor: string;
  sectionType: string;
  variantsConfig: Record<string, VariantConfig>;
  api: {
    getAll?: () => Promise<any>;
    getByVariant?: (variantId: string) => Promise<any>;
    getActive?: () => Promise<any>;
    create?: (data: any) => Promise<any>;
    fetchByUniversitySettingsId?: (id: number) => Promise<any>;
  };
  ui: {
    showCreateButton: boolean;
    isDirectForm: boolean;
    emptyMessage: string;
    sidebarTitle: string;
  };
}

// Common constants shared across variants
export const COMMON_CONSTANTS = {
  VALIDATION_ERROR_TITLE: "Validation Error",
  VALIDATION_ERROR_DESCRIPTION: "Please fix the errors before saving",
  CONFIG_NOT_FOUND_ERROR: "Widget configuration not found",
  UPDATE_METHOD_NOT_CONFIGURED_ERROR: "Update method not configured",
};

